import { PERMS } from '@/constants/permissions';

/**
 * Fuente ÚNICA de verdad de "¿este usuario puede ver el Portal del Asesor?".
 *
 * Predicado PURO consumido por los dos sitios que deciden la visibilidad del
 * portal — y que históricamente divergían (este fix, drift portal-asesor):
 *   - `useCanSeePortalAsesor` (hook) → guard de la ruta `/portal-asesor`.
 *   - `showIf` del item de menú en `pages/Layout.jsx` → link del sidebar.
 *
 * El bug: el menú comprobaba `asesor.view` + owner/admin-multi, mientras que el
 * hook (tras BE PR #302) comprobaba `firmMember`/`effectiveRole` + owner/admin-multi.
 * Un asesor estructural (`firmMember`) recién entrado, sin company activa ni
 * `asesor.view` cargado, pasaba el guard de la ruta pero NO veía el link del
 * sidebar (y, a la inversa, un user con solo `asesor.view` veía el link pero la
 * ruta lo rebotaba). Antecedente del mismo patrón de drift: `useUserCompaniesCount.ts`.
 *
 * ⚠️ NO dupliques esta condición inline. Si necesitas el gate, llama aquí.
 *
 * Dos MODOS de acceso (ver `portalAsesorMode`):
 *
 *   'firm' — ASESORÍA / DESPACHO real. Cualquiera de:
 *     (0) Asesor estructural — `firmMember === true`. Cross-tenant: vale aun sin
 *         company activa (asesor recién registrado con `company_id=''`).
 *     (1) Asesor externo en company activa — `effectiveRole === 'accountant_external'`.
 *     (2) Permiso explícito `asesor.view` — el BE solo lo asigna al rol
 *         "Accountant External" (rbacSeed); el acceso a datos lo re-valida el BE.
 *
 *   'group' — GRUPO PROPIO (NO es un despacho):
 *     (3) Owner / admin con > 1 empresa propia ("CEO con varias sociedades del
 *         grupo"). El backend `/api/asesor/*` es firm-only (cartera vacía a un
 *         no-firm-member) y el ARIA del despacho lo rechaza (403), así que la UI
 *         presenta este modo con OTRO branding ("Mis empresas") y oculta las
 *         pantallas propias de despacho (honorarios, equipo, notificaciones de
 *         organismos, reclamar documentación a clientes).
 *
 * `canSeePortalAsesor` = "tiene acceso en ALGÚN modo"; `portalAsesorMode`
 * distingue cuál para que el shell adapte nombre y pestañas.
 */
export type PortalAsesorMode = 'firm' | 'group' | null;

export interface PortalAsesorGateCtx {
  user: { firmMember?: boolean; effectiveRole?: string; role?: string | null; permissions?: string[] } | null | undefined;
  hasPermission: (code: string) => boolean;
  userCompaniesCount: number;
}

export function portalAsesorMode({ user, userCompaniesCount }: PortalAsesorGateCtx): PortalAsesorMode {
  if (!user) return null;
  // Despacho REAL: pertenece a una asesoría (accounting_firm_members). Es la
  // ÚNICA señal que el backend (`resolveAdvisorPortfolio`) usa para "firm", así
  // que debe ir primero — y por encima de group.
  if (user.firmMember === true) return 'firm';
  // Grupo propio: owner/admin de varias sociedades SIN ser firm member. Va antes
  // que accountant_external/asesor.view para que una cuenta normal con varias
  // empresas (que puede tener asesor.view por su rol) NO se presente como
  // despacho ("Mi despacho"/"Modo Asesor"), sino como "Mis empresas". Alinea el
  // FE con el carril owner del backend.
  if ((user.role === 'owner' || user.role === 'admin') && userCompaniesCount > 1) return 'group';
  // Asesor externo en god-mode / permiso de despacho sin ser firm member.
  if (user.effectiveRole === 'accountant_external') return 'firm';
  // asesor.view EXPLÍCITO: NO usar `hasPermission`, que hace bypass god-mode para
  // system admin (role==='admin' → true para cualquier code). Un admin normal de
  // una sola empresa caería aquí y vería "Portal del Asesor" por error. El BE solo
  // asigna asesor.view al rol "Accountant External" (rbacSeed), así que comprobamos
  // el permiso REAL en la lista del usuario.
  if ((user.permissions ?? []).includes(PERMS.ASESOR_VIEW)) return 'firm';
  return null;
}

export function canSeePortalAsesor(ctx: PortalAsesorGateCtx): boolean {
  return portalAsesorMode(ctx) !== null;
}

/**
 * ¿La página de entrada `/` debe redirigir al portal (asesor / mis-empresas) en
 * vez del Home? Un asesor de despacho real (`firmMember`) SIEMPRE — el despacho
 * es su home aunque ahora solo gestione una empresa. Una cuenta normal solo si
 * gestiona MÁS de una empresa (modo 'group'). Una cuenta con una sola empresa
 * (aun con asesor.view / accountant_external) cae al Home.
 *
 * NO oculta el link del portal ni el guard de la ruta — solo decide el landing.
 */
export function shouldLandOnPortal(ctx: PortalAsesorGateCtx): boolean {
  if (!canSeePortalAsesor(ctx)) return false;
  if (ctx.user?.firmMember === true) return true;
  return ctx.userCompaniesCount > 1;
}

export default canSeePortalAsesor;
